"use client";

import { useEffect, useRef } from "react";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { getQuizState } from "../utils/utils";
import { type TouchpointType } from "@/lib/quiz-attribution";

const viewedTouchpoints = new Set<TouchpointType>();

/**
 * Fire quiz_touchpoint_viewed once per page load for the given touchpoint.
 * Pass `dedupe: false` to fire on every impression instead.
 */
export function fireQuizTouchpointImpression(
  type: TouchpointType,
  { dedupe = true }: { dedupe?: boolean } = {},
): void {
  if (dedupe && viewedTouchpoints.has(type)) return;
  if (dedupe) viewedTouchpoints.add(type);
  const state = getQuizState();
  sendEvent(EventNames.QUIZ_TOUCHPOINT_VIEWED, {
    touchpoint_type: type,
    user_state: state.status,
    page_url: window.location.href,
  });
}

/**
 * Returns a ref to attach to the touchpoint's root DOM element.
 * Fires quiz_touchpoint_viewed when ≥50% of the element enters the viewport.
 * Pass `fireEveryView: true` to fire on every viewport entry instead of once per page load.
 */
export function useQuizTouchpointImpression<T extends HTMLElement = HTMLElement>(
  type: TouchpointType,
  { fireEveryView = false }: { fireEveryView?: boolean } = {},
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!fireEveryView && viewedTouchpoints.has(type)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fireQuizTouchpointImpression(type, { dedupe: !fireEveryView });
          if (!fireEveryView) observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [type, fireEveryView]);

  return ref;
}

/**
 * For touchpoints controlled by a `visible` boolean (side_chip, floating_banner).
 * Fires quiz_touchpoint_viewed once per page load when `visible` first becomes true.
 */
export function useQuizTouchpointImpressionVisible(
  type: TouchpointType,
  visible: boolean,
): void {
  useEffect(() => {
    if (!visible) return;
    fireQuizTouchpointImpression(type);
  }, [type, visible]);
}
